import { useState, useEffect } from 'react'
import { X, PencilLine, Trash2 } from 'lucide-react'
import { Card, Badge, Button, Input, Select, SearchBar, ConfirmDialog, Modal } from '../../../shared/components/UI'
import { usersApi, unitsApi } from '../../../shared/lib/api'

const ROLE_LABEL = { metallic_master: 'Metallic Master', loading_supervisor: 'Loading Supervisor', admin: 'Admin', company_head: 'Company Head' }
const ROLE_BADGE = { metallic_master: 'navy', loading_supervisor: 'blue', admin: 'blue', company_head: 'teal' }

const emptyForm = { name: '', mobile: '', role: 'metallic_master', unitId: '', pin: '', status: 'active' }

export default function UsersPage() {
  const [users, setUsers] = useState([])
  const [units, setUnits] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [deletingId, setDeletingId] = useState(null)
  const [acting, setActing] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    Promise.all([
      usersApi.list(),
      unitsApi.list()
    ]).then(([userRes, unitRes]) => {
      setUsers(userRes.data.data)
      setUnits(unitRes.data.data)
    }).finally(() => setLoading(false))
  }, [])

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }))

  const visible = users.filter((u) =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.mobile.includes(search)
  )

  const startNew = () => {
    setEditing('new')
    setForm(emptyForm)
  }

  const startEdit = (u) => {
    setEditing(u.id)
    setForm({
      name: u.name,
      mobile: u.mobile,
      role: u.role,
      unitId: u.unit_id || '',
      pin: '',
      status: u.status
    })
  }

  const cancel = () => {
    setEditing(null)
    setForm(emptyForm)
  }

  const save = async () => {
    if (!form.name.trim() || !form.mobile.trim()) return

    const payload = {
      ...form,
      mobile: form.mobile.replace(/\s/g, '')
    }

    setActing(true)
    setError('')
    try {
      if (editing === 'new') {
        await usersApi.create(payload)
      } else {
        await usersApi.update(editing, payload)
      }
      const res = await usersApi.list()
      setUsers(res.data.data)
      cancel()
    } catch (err) {
      setError(err?.response?.data?.message || 'Save failed.')
      console.error(err)
    } finally {
      setActing(false)
    }
  }

  const handleDelete = async () => {
    if (!deletingId) return
    setActing(true)
    setError('')
    try {
      await usersApi.delete(deletingId)
      setUsers(p => p.filter(u => u.id !== deletingId))
      setDeletingId(null)
    } catch (err) {
      setError(err?.response?.data?.message || 'Delete failed.')
    } finally {
      setActing(false)
    }
  }

  if (loading) return <div className="p-8 text-center text-gray-400">Loading users...</div>

  return (
    <>
    <div className="space-y-4">
      <div>
          <SearchBar value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or mobile...">
            <Button onClick={startNew}>+ Add User</Button>
          </SearchBar>

          <Card title={`All Users (${visible.length})`} bodyClass="!p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>Name</th><th>Mobile</th><th>Role</th><th>Unit</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {visible.map((u) => (
                    <tr key={u.id}>
                      <td className="font-medium">{u.name}</td>
                      <td className="td-mono">{u.mobile}</td>
                      <td><Badge variant={ROLE_BADGE[u.role]}>{ROLE_LABEL[u.role] || u.role}</Badge></td>
                      <td className="text-gray-500 text-xs">{u.unit?.name || '-'}</td>
                      <td><Badge variant={u.status === 'active' ? 'green' : 'amber'}>{u.status === 'active' ? 'Active' : 'Inactive'}</Badge></td>
                      <td className="pr-4">
                        <div className="flex justify-start gap-1">
                          <button 
                            onClick={() => startEdit(u)}
                            className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition-all"
                            title="Edit"
                          >
                            <PencilLine size={16} />
                          </button>
                          <button 
                            onClick={() => setDeletingId(u.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>

      {editing && (
        <Modal
          isOpen={!!editing}
          onClose={cancel}
          title={editing === 'new' ? 'Add New Team Member' : 'Edit User Profile'}
          size="md"
          confirmClose={form.name.trim() !== '' || form.mobile.trim() !== ''}
        >
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <Input label="Full Name" value={form.name} onChange={f('name')} placeholder="e.g. Ravi Shah" className="rounded-xl" />
              <Input label="Mobile Number" value={form.mobile} onChange={f('mobile')} placeholder="10-digit mobile" type="tel" className="rounded-xl" />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <Select label="System Role" value={form.role} onChange={f('role')} options={[
                { value: 'metallic_master', label: 'Metallic Master' },
                { value: 'loading_supervisor', label: 'Loading Supervisor' },
                { value: 'admin', label: 'Admin' },
                { value: 'company_head', label: 'Company Head' },
              ]} className="rounded-xl" />
              <Select
                label="Assign to Unit"
                value={form.unitId}
                onChange={f('unitId')}
                options={[
                  { label: 'No Unit Assignment', value: '' },
                  ...units.map((u) => ({ label: u.name, value: u.id }))
                ]}
                className="rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label={editing === 'new' ? 'Login PIN (4-digit)' : 'New PIN (optional)'}
                value={form.pin}
                onChange={f('pin')}
                placeholder="4-digit PIN"
                type="password"
                maxLength={4}
                className="rounded-xl"
              />
              <Select
                label="Account Status"
                value={form.status}
                onChange={f('status')}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' }
                ]}
                className="rounded-xl"
              />
            </div>

            <div className="p-4 bg-gray-50 border border-gray-100 rounded-2xl text-[11px] text-gray-500 leading-relaxed">
              <strong>Note:</strong> PINs must be exactly 4 digits. Mobile numbers are used as the primary login identifier.
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={save} disabled={acting || !form.name.trim() || !form.mobile.trim()} className="flex-1 py-4 rounded-2xl font-black shadow-lg shadow-brand-100">
                {acting ? 'Saving...' : (editing === 'new' ? 'Create Account' : 'Save Changes')}
              </Button>
              <Button variant="secondary" onClick={cancel} disabled={acting} className="flex-1 py-4 rounded-2xl border-gray-100 text-gray-500">Cancel</Button>
            </div>
          </div>
        </Modal>
      )}
    <ConfirmDialog
        isOpen={!!deletingId}
        onClose={() => { setDeletingId(null); setError(''); }}
        onConfirm={handleDelete}
        title="Delete User?"
        message="This will permanently remove this user account. They will no longer be able to log in."
        confirmText="Yes, Delete"
        isLoading={acting}
        error={error}
      />
    </>
  )
}
